import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { SxProps } from "@mui/system";
import { Box } from "@mui/material";

interface GSAPButtonProps {
  label: string;
  onClick?: () => void;
  sx?: SxProps;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
}

const StyledButton = styled.a<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 22px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-decoration: none;
  color: white;
  background: transparent;
  border-radius: 6.25rem;
  position: relative;
  overflow: hidden;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: color 0.15s ease-in-out, background 0.2s ease-in-out;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  border: 2px solid white;

  &:hover {
    color: black;
    background: white;
  }

  .button__label {
    position: relative;
    transition: color 0.15s ease-in-out;
  }

  &:hover .button__label {
    color: black;
  }

  .button__flair {
    position: absolute;
    inset: 0;
    pointer-events: none;
    transform: scale(0);
    transform-origin: 0 0;
    will-change: transform;
  }

  .button__flair:before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 170%;
    aspect-ratio: 1/1;
    background: white;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  .button__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: filter 0.2s ease-in-out;
  }

  .button__icon svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }

  &:hover .button__icon {
    filter: invert(1);
  }
`;

const GSAPButton: React.FC<GSAPButtonProps> = ({ label, onClick, sx, startIcon, endIcon, disabled }) => {
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const flairRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (disabled) return;

    const button = buttonRef.current;
    const flair = flairRef.current;
    if (!button || !flair) return;

    const xSet = gsap.quickSetter(flair, "xPercent");
    const ySet = gsap.quickSetter(flair, "yPercent");

    const getXY = (e: MouseEvent) => {
      const { left, top, width, height } = button.getBoundingClientRect();
      return {
        x: gsap.utils.mapRange(0, width, 0, 100, e.clientX - left),
        y: gsap.utils.mapRange(0, height, 0, 100, e.clientY - top),
      };
    };

    button.addEventListener("mouseenter", (e) => {
      const { x, y } = getXY(e);
      xSet(x);
      ySet(y);
      gsap.to(flair, { scale: 1, duration: 0.4, ease: "power2.out" });
    });

    button.addEventListener("mouseleave", (e) => {
      const { x, y } = getXY(e);
      gsap.killTweensOf(flair);
      gsap.to(flair, {
        xPercent: x > 90 ? x + 20 : x < 10 ? x - 20 : x,
        yPercent: y > 90 ? y + 20 : y < 10 ? y - 20 : y,
        scale: 0,
        duration: 0.3,
        ease: "power2.out",
      });
    });

    button.addEventListener("mousemove", (e) => {
      const { x, y } = getXY(e);
      gsap.to(flair, { xPercent: x, yPercent: y, duration: 0.4, ease: "power2" });
    });
  }, [disabled]);

  return (
    <Box component="span" sx={sx}>
      <StyledButton href="#" ref={buttonRef} onClick={onClick} disabled={disabled}>
        {startIcon && <span className="button__icon">{startIcon}</span>}
        <span ref={flairRef} className="button__flair"></span>
        <span className="button__label">{label}</span>
        {endIcon && <span className="button__icon">{endIcon}</span>}
      </StyledButton>
    </Box>
  );
};

export default GSAPButton;
